import type { OrderData, PagoParcial } from '../types/orderFormTypes'
import { ROLE_ID_ADMIN, isHomecenterOrderRole } from '../constants/roleIds'

/** Estados en los que el formulario de edición permite modificar todos los campos. */
export const ORDER_STATUSES_FULLY_EDITABLE_IN_EDIT = [
  'pending',
  'verify_transaction'
] as const

export type OrderStatusFullyEditableInEdit =
  (typeof ORDER_STATUSES_FULLY_EDITABLE_IN_EDIT)[number]

/** Plan Separe en abonado: se pueden agregar/editar abonos no verificados. */
export const ORDER_STATUSES_EDITABLE_FOR_PAYMENT = [
  'pending',
  'verify_transaction',
  'abonado'
] as const

export type OrderStatusEditableForPayment = (typeof ORDER_STATUSES_EDITABLE_FOR_PAYMENT)[number]

function normalizeOrderStatus(status: string | undefined): string {
  return (status || '').toString().trim().toLowerCase()
}

export function isOrderStatusFullyEditableInEdit(status: string | undefined): boolean {
  const s = normalizeOrderStatus(status)
  return ORDER_STATUSES_FULLY_EDITABLE_IN_EDIT.includes(s as OrderStatusFullyEditableInEdit)
}

/**
 * En edición: bloquea el formulario salvo transportadora, guía y (si aplica) pagos parciales.
 */
export function isOrderFormRestrictedInEdit(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined
): boolean {
  if (mode !== 'edit') return false
  return !isOrderStatusFullyEditableInEdit(status)
}

/**
 * Restricción de edición por estado del pedido.
 * Aplica a todos los roles (incluido admin): fuera de pending/verify_transaction el form se bloquea.
 */
export function isOrderFormRestrictedForUser(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined,
  _userRoleId?: number
): boolean {
  return isOrderFormRestrictedInEdit(mode, status)
}

/**
 * Sección de productos en edición: búsqueda, líneas, Cliente Neto y Retención.
 * Roles Homecenter (16, 26) pueden editarlos aunque el resto del formulario esté restringido.
 */
export function canEditOrderProductsForUser(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined,
  userRoleId: number | undefined | null
): boolean {
  if (mode === 'view') return false
  if (mode === 'create') return true
  if (userRoleId === ROLE_ID_ADMIN) return true
  if (!isOrderFormRestrictedInEdit(mode, status)) return true
  return isHomecenterOrderRole(userRoleId)
}

export function isOrderStatusEditableForPayment(status: string | undefined): boolean {
  const s = normalizeOrderStatus(status)
  return ORDER_STATUSES_EDITABLE_FOR_PAYMENT.includes(s as OrderStatusEditableForPayment)
}

export function isPagoVerificado(value: unknown): boolean {
  return Number(value) === 1
}

export function getOrderPagoVerificado(formData: OrderData): boolean {
  const root = (formData as { pago_verificado?: unknown }).pago_verificado
  if (root !== undefined && root !== null) {
    return isPagoVerificado(root)
  }
  return false
}

function isPartOrPlanSeparePaymentMethod(paymentMethod: string | undefined): boolean {
  const pm = (paymentMethod || '').toString().trim().toLowerCase()
  return pm === 'part' || pm === 'plan_separe'
}

function hasAnyPagoParcialVerificado(pagos: PagoParcial[] | undefined): boolean {
  if (!Array.isArray(pagos) || pagos.length === 0) return false
  return pagos.some((p) => isPagoVerificado(p.pago_verificado))
}

export function canEditPartialPaymentsSection(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined,
  paymentMethod: string | undefined
): boolean {
  if (mode === 'view') return false
  if (mode === 'create') return true
  if (isOrderStatusEditableForPayment(status)) return true
  if (isPartOrPlanSeparePaymentMethod(paymentMethod)) return true
  return false
}

export function canEditMainPaymentMethod(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined,
  options?: {
    paymentMethod?: string
    pagosParciales?: PagoParcial[]
  }
): boolean {
  if (mode === 'create') return true
  if (mode === 'view') return false
  if (!isOrderStatusFullyEditableInEdit(status)) return false

  const pm = options?.paymentMethod
  if (
    isPartOrPlanSeparePaymentMethod(pm) &&
    hasAnyPagoParcialVerificado(options?.pagosParciales)
  ) {
    return false
  }

  return true
}

export function getMainPaymentMethodEditBlockReason(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined,
  options?: {
    paymentMethod?: string
    pagosParciales?: PagoParcial[]
  }
): string | undefined {
  if (canEditMainPaymentMethod(mode, status, options)) return undefined
  if (mode !== 'edit') return undefined

  if (!isOrderStatusFullyEditableInEdit(status)) {
    return 'Solo se puede modificar en estado Pendiente de pago o Verificar transacción'
  }

  if (
    isPartOrPlanSeparePaymentMethod(options?.paymentMethod) &&
    hasAnyPagoParcialVerificado(options?.pagosParciales)
  ) {
    return 'No se puede cambiar: ya hay al menos un pago o abono verificado'
  }

  return undefined
}

/**
 * Fila de abono/pago parcial bloqueada para edición/eliminar.
 * - Verificado (pago_verificado = 1): nunca editable.
 * - No verificado: editable si la sección de pagos parciales está habilitada.
 */
export function isPagoParcialRowLocked(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined,
  pago: PagoParcial,
  paymentMethod?: string
): boolean {
  if (mode === 'view') return true
  if (mode === 'create') return false
  if (isPagoVerificado(pago.pago_verificado)) return true
  if (canEditPartialPaymentsSection(mode, status, paymentMethod)) return false
  return true
}

/** Permite el botón "+ Agregar abono/pago" en edición. */
export function canAgregarPagoParcial(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined,
  paymentMethod?: string
): boolean {
  if (mode === 'view') return false
  if (mode === 'create') return true
  return canEditPartialPaymentsSection(mode, status, paymentMethod)
}

export function canShowBoldRegenerateButton(
  mode: 'view' | 'edit' | 'create',
  pagoVerificado: boolean,
  status?: string,
  paymentMethod?: string
): boolean {
  if (mode !== 'edit') return false
  if (pagoVerificado) return false
  return canEditPartialPaymentsSection(mode, status, paymentMethod)
}

export function shouldShowBoldCheckoutLink(status: string | undefined): boolean {
  const s = normalizeOrderStatus(status)
  return s === 'pending' || s === 'processing' || s === 'verify_transaction'
}

export function isOrderFieldLockedInEdit(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined
): boolean {
  if (mode === 'view') return true
  return isOrderFormRestrictedInEdit(mode, status)
}

export function canEditShippingCarrierOrGuia(mode: 'view' | 'edit' | 'create'): boolean {
  return mode === 'edit' || mode === 'create'
}
